import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PlusIcon, Trash01Icon as TrashIcon, ClockIcon } from '@untitledui-icons/react/line'
import { Button } from '@/components/base/buttons'
import { Input } from '@/components/ui/Input'
import { cn } from '@/lib/utils'
import type { AvailabilitySchedule } from './transferOverrides'

const DAYS_OF_WEEK = [
  { id: 1, name: 'Monday', short: 'Mon' },
  { id: 2, name: 'Tuesday', short: 'Tue' },
  { id: 3, name: 'Wednesday', short: 'Wed' },
  { id: 4, name: 'Thursday', short: 'Thu' },
  { id: 5, name: 'Friday', short: 'Fri' },
  { id: 6, name: 'Saturday', short: 'Sat' },
  { id: 7, name: 'Sunday', short: 'Sun' },
]

const is24Hours = (schedule: AvailabilitySchedule) =>
  schedule.start_time === '00:00' && schedule.end_time === '23:59'

export interface AvailabilityScheduleEditorProps {
  schedules: AvailabilitySchedule[]
  onChange: (schedules: AvailabilitySchedule[]) => void
  errors?: Array<{ index: number; error: string }>
  /**
   * Restrict the day picker to these weekdays (1=Monday..7=Sunday), e.g. the
   * days occurring in a short exception period. Days outside the set are shown
   * greyed out; extending the period unlocks them. Omit for the full week.
   */
  selectableDays?: number[]
}

export function AvailabilityScheduleEditor({ schedules, onChange, errors, selectableDays }: AvailabilityScheduleEditorProps) {
  const { t } = useTranslation()
  const [is24HourModes, setIs24HourModes] = useState<boolean[]>(() => schedules.map(is24Hours))

  const isRestricted = selectableDays !== undefined && selectableDays.length < 7
  const isLockedToSingleDay = isRestricted && selectableDays.length === 1

  const handleDayToggle = (scheduleIndex: number, day: number) => {
    const updated = [...schedules]
    const currentDays = updated[scheduleIndex].days
    updated[scheduleIndex] = {
      ...updated[scheduleIndex],
      days: currentDays.includes(day)
        ? currentDays.filter((d) => d !== day)
        : [...currentDays, day].sort((a, b) => a - b),
    }
    onChange(updated)
  }

  const handleTimeChange = (scheduleIndex: number, type: 'start_time' | 'end_time', value: string) => {
    const updated = [...schedules]
    updated[scheduleIndex] = {
      ...updated[scheduleIndex],
      [type]: value,
    }
    onChange(updated)
  }

  const handleNumberChange = (scheduleIndex: number, value: string) => {
    const updated = [...schedules]
    updated[scheduleIndex] = {
      ...updated[scheduleIndex],
      number: value,
    }
    onChange(updated)
  }

  const handleAdd = () => {
    // With a restricted day set the locked pills cannot be clicked, so seed them
    const seedDays = isRestricted ? [...selectableDays] : []
    onChange([...schedules, { days: seedDays, start_time: '09:00', end_time: '17:00' }])
    setIs24HourModes([...is24HourModes, false])
  }

  const handleRemove = (idx: number) => {
    onChange(schedules.filter((_, i) => i !== idx))
    setIs24HourModes(is24HourModes.filter((_, i) => i !== idx))
  }

  const set24HourMode = (idx: number) => {
    const updatedModes = [...is24HourModes]
    updatedModes[idx] = true
    setIs24HourModes(updatedModes)

    const updated = [...schedules]
    updated[idx] = {
      ...updated[idx],
      start_time: '00:00',
      end_time: '23:59',
    }
    onChange(updated)
  }

  const disable24HourMode = (idx: number) => {
    const updatedModes = [...is24HourModes]
    updatedModes[idx] = false
    setIs24HourModes(updatedModes)
  }

  return (
    <div>
      {schedules.map((schedule, scheduleIndex) => (
        <div key={scheduleIndex} className="mb-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
          <div className="flex items-start justify-between mb-3">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('resources.assistants.transfer_flows.modal.schedule_number', { number: scheduleIndex + 1 })}
            </span>
            <Button
              color="tertiary"
              size="sm"
              onPress={() => handleRemove(scheduleIndex)}
              className="p-1"
              iconLeading={TrashIcon}
            />
          </div>

          {/* Days selection */}
          <div className="mb-4">
            <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">
              {t('resources.assistants.transfer_flows.modal.days')}
            </label>
            <div className="flex flex-wrap gap-2">
              {DAYS_OF_WEEK.map(day => {
                const outsidePeriod = isRestricted && !selectableDays.includes(day.id)
                const locked = outsidePeriod || isLockedToSingleDay
                return (
                  <button
                    key={day.id}
                    type="button"
                    disabled={locked}
                    onClick={() => handleDayToggle(scheduleIndex, day.id)}
                    className={cn(
                      'px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
                      schedule.days.includes(day.id)
                        ? 'bg-brand-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700',
                      outsidePeriod && 'opacity-40 cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-800',
                      !outsidePeriod && isLockedToSingleDay && 'cursor-default'
                    )}
                  >
                    {t(`resources.assistants.days.${day.short.toLowerCase()}`)}
                  </button>
                )
              })}
            </div>
            {errors?.some(e => e.index === scheduleIndex) && (
              <p className="mt-1 text-sm text-error-600">
                {errors?.find(e => e.index === scheduleIndex)?.error}
              </p>
            )}
          </div>

          {/* Time selection */}
          <div className="mb-2">
            {is24HourModes[scheduleIndex] ? (
              <div className="flex items-center gap-2">
                <ClockIcon className="h-4 w-4 text-brand-500" />
                <span className="text-sm text-brand-600 dark:text-brand-400">
                  {t('resources.assistants.transfer_flows.modal.active_24h')}
                </span>
                <Button
                  color="link-color"
                  size="sm"
                  onPress={() => disable24HourMode(scheduleIndex)}
                  className="text-sm"
                >
                  {t('resources.assistants.transfer_flows.modal.customize')}
                </Button>
              </div>
            ) : (
              <Button
                color="secondary"
                size="sm"
                onPress={() => set24HourMode(scheduleIndex)}
                iconLeading={ClockIcon}
              >
                {t('resources.assistants.transfer_flows.modal.set_24h')}
              </Button>
            )}
          </div>

          {!is24HourModes[scheduleIndex] && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                  {t('resources.assistants.transfer_flows.modal.start_time')}
                </label>
                <Input
                  type="time"
                  value={schedule.start_time}
                  onChange={(value) => handleTimeChange(scheduleIndex, 'start_time', value)}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                  {t('resources.assistants.transfer_flows.modal.end_time')}
                </label>
                <Input
                  type="time"
                  value={schedule.end_time}
                  onChange={(value) => handleTimeChange(scheduleIndex, 'end_time', value)}
                />
              </div>
            </div>
          )}

          {/* Optional per-window destination */}
          <div className="mt-4">
            <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
              {t('resources.assistants.transfer_flows.modal.schedule_phone_number')}
            </label>
            <Input
              type="tel"
              value={schedule.number ?? ''}
              onChange={(value) => handleNumberChange(scheduleIndex, value)}
              placeholder={t('resources.assistants.transfer_flows.modal.phone_placeholder')}
            />
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {t('resources.assistants.transfer_flows.modal.schedule_phone_number_description')}
            </p>
          </div>
        </div>
      ))}

      <Button color="secondary" size="sm" onPress={handleAdd} iconLeading={PlusIcon}>
        {t('resources.assistants.transfer_flows.modal.add_availability')}
      </Button>
    </div>
  )
}

export default AvailabilityScheduleEditor
